import React, { useState, useEffect } from 'react';
import { db } from './firebase/config';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { Calendar, AlertCircle, Check, X, ListChecks } from 'lucide-react';
import { getAllCohorts, setSubmissionModeOverride, resolveEnabledHomeworkTypes, setEnabledHomeworkTypes } from './firebase/cohorts';
import { getHomeworkNames, sortHomeworkTypes } from './firebase/settings';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const emptyParts = () => ({ day: '', month: '', year: '', hour: '17', minute: '00' });

const partsToIso = ({ day, month, year, hour, minute }) => {
  if (!day || !month || !year) return null;
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const isoToParts = (iso) => {
  if (!iso) return emptyParts();
  const [datePart, timePart] = iso.split('T');
  const [year, month, day] = datePart.split('-');
  const [hour, minute] = (timePart || '17:00').split(':');
  return { day, month, year, hour, minute };
};

const getDaysInMonth = (month, year) => {
  if (!month || !year) return 31;
  return new Date(parseInt(year), parseInt(month), 0).getDate();
};

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear + 1, currentYear + 2].map(String);
const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const selectStyle = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  background: 'white',
  cursor: 'pointer',
  outline: 'none',
};

const modeSelectStyle = {
  ...selectStyle,
  minWidth: '150px',
  marginLeft: 'auto',
};

const actionButtonStyle = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '6px',
  fontWeight: '600',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

// One date+time setting (a deadline, or a release date) with its own
// Save/Clear - each is saved individually, same as before.
const DateTimeSetting = ({ label, savedIso, defaultHour, onSave, onClear, children }) => {
  const toParts = (iso) => (iso ? isoToParts(iso) : { ...emptyParts(), hour: defaultHour });
  const [parts, setParts] = useState(() => toParts(savedIso));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Reset local state when saved value changes from parent
  useEffect(() => {
    setParts(toParts(savedIso));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedIso]);

  const update = (field, value) => {
    setParts(prev => {
      const next = { ...prev, [field]: value };
      // If day is now out of range for new month/year, clamp it
      const maxDay = getDaysInMonth(next.month, next.year);
      if (next.day && parseInt(next.day) > maxDay) {
        next.day = String(maxDay).padStart(2, '0');
      }
      return next;
    });
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async () => {
    const iso = partsToIso(parts);
    if (!iso) return;
    setSaving(true);
    await onSave(iso);
    setSaving(false);
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleClear = async () => {
    setSaving(true);
    await onClear();
    setParts(toParts(null));
    setDirty(false);
    setSaving(false);
  };

  const daysInMonth = getDaysInMonth(parts.month, parts.year);
  const days = Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, '0'));
  const isComplete = parts.day && parts.month && parts.year;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', width: '100%' }}>
      <span style={{ width: '64px', fontSize: '13px', fontWeight: '600', color: '#4b5563' }}>{label}</span>

      {/* Day */}
      <select style={selectStyle} value={parts.day} onChange={e => update('day', e.target.value)}>
        <option value="">Day</option>
        {days.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
      </select>

      {/* Month */}
      <select style={{ ...selectStyle, minWidth: '110px' }} value={parts.month} onChange={e => update('month', e.target.value)}>
        <option value="">Month</option>
        {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>

      {/* Year */}
      <select style={selectStyle} value={parts.year} onChange={e => update('year', e.target.value)}>
        <option value="">Year</option>
        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>

      <span style={{ color: '#9ca3af', fontWeight: '600' }}>at</span>

      {/* Hour */}
      <select style={selectStyle} value={parts.hour} onChange={e => update('hour', e.target.value)}>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>

      <span style={{ color: '#6b7280', fontWeight: '700' }}>:</span>

      {/* Minute */}
      <select style={selectStyle} value={parts.minute} onChange={e => update('minute', e.target.value)}>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>

      {children}

      {dirty && isComplete && (
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ ...actionButtonStyle, background: '#2563eb', color: 'white', cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          <Check size={14} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      )}

      {saved && (
        <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: '600' }}>Saved!</span>
      )}

      {savedIso && !dirty && (
        <button
          onClick={handleClear}
          disabled={saving}
          style={{ ...actionButtonStyle, padding: '8px 12px', background: 'white', color: '#dc2626', border: '1px solid #fca5a5', cursor: saving ? 'not-allowed' : 'pointer', marginLeft: children ? 0 : 'auto' }}
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

const DeadlineRow = ({ type, savedIso, releaseIso, onSave, onClear, modeOverride, onSetModeOverride }) => {
  const releaseAfterDeadline = savedIso && releaseIso && releaseIso >= savedIso;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      background: savedIso ? '#f0fdf4' : '#f9fafb',
      border: `1px solid ${savedIso ? '#86efac' : '#e5e7eb'}`,
      borderRadius: '8px',
    }}>
      {/* Label */}
      <span style={{ width: '40px', fontWeight: '700', fontSize: '15px', color: '#1f2937', paddingTop: '8px' }}>
        {type}
      </span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
        <DateTimeSetting
          label="Deadline"
          savedIso={savedIso}
          defaultHour="17"
          onSave={(iso) => onSave(type, iso)}
          onClear={() => onClear(type)}
        >
          {/* Submission mode override for this cohort - defaults to whatever the
              homework type's global default is set to in Head Marker Settings */}
          <select
            style={modeSelectStyle}
            value={modeOverride || ''}
            onChange={e => onSetModeOverride(type, e.target.value || null)}
            title="Override the submission mode for this homework, for this cohort only"
          >
            <option value="">Use homework default</option>
            <option value="pdf">Override: PDF Upload</option>
            <option value="exam">Override: Exam (Timed)</option>
          </select>
        </DateTimeSetting>

        {/* Release date - students can't see, start, or download this
            homework's questions until then. Optional: blank = open now. */}
        <DateTimeSetting
          label="Opens"
          savedIso={releaseIso}
          defaultHour="09"
          onSave={(iso) => onSave(`${type}_availableFrom`, iso)}
          onClear={() => onClear(`${type}_availableFrom`)}
        />
        {releaseAfterDeadline && (
          <span style={{ fontSize: '12px', color: '#b45309', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <AlertCircle size={14} />
            Opens on or after its deadline - check the dates.
          </span>
        )}
      </div>
    </div>
  );
};

// cohortId (optional): open on that cohort with the picker hidden - used when
// opened from a cohort's own page in the Cohorts tab
const DeadlineManager = ({ cohortId } = {}) => {
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [deadlines, setDeadlines] = useState({});
  const [modeOverrides, setModeOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [homeworkTypes, setHomeworkTypes] = useState([]);

  useEffect(() => {
    loadCohorts();
    getHomeworkNames()
      .then(names => {
        setHomeworkTypes(sortHomeworkTypes(Object.keys(names)));
      })
      .catch(err => console.error('Error loading homework names:', err));
  }, []);

  useEffect(() => {
    if (selectedCohort) loadDeadlines();
  }, [selectedCohort]);

  const loadCohorts = async () => {
    try {
      setLoading(true);
      const cohortsData = await getAllCohorts();
      setCohorts(cohortsData);
      if (cohortId && cohortsData.some(c => c.id === cohortId)) setSelectedCohort(cohortId);
      else if (cohortsData.length > 0) setSelectedCohort(cohortsData[0].id);
    } catch (err) {
      console.error('Error loading cohorts:', err);
      setError('Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  };

  const loadDeadlines = async () => {
    if (!selectedCohort) return;
    try {
      const deadlineDoc = await getDoc(doc(db, 'cohorts', selectedCohort, 'deadlines', 'current'));
      if (deadlineDoc.exists()) {
        const data = deadlineDoc.data();
        const formatted = {};
        const modes = {};
        Object.keys(data).forEach(key => {
          if (key.endsWith('_modeOverride')) {
            modes[key.replace('_modeOverride', '')] = data[key] || null;
          } else if (data[key]) {
            const date = data[key].toDate();
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            formatted[key] = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        });
        setDeadlines(formatted);
        setModeOverrides(modes);
      } else {
        setDeadlines({});
        setModeOverrides({});
      }
    } catch (err) {
      console.error('Error loading deadlines:', err);
      setError('Failed to load deadlines');
    }
  };

  const handleSave = async (homeworkType, iso) => {
    try {
      setError('');
      const deadline = Timestamp.fromDate(new Date(iso));
      await setDoc(doc(db, 'cohorts', selectedCohort, 'deadlines', 'current'), {
        [homeworkType]: deadline
      }, { merge: true });
      setDeadlines(prev => ({ ...prev, [homeworkType]: iso }));
    } catch (err) {
      console.error('Error saving deadline:', err);
      setError(`Failed to save ${homeworkType.replace("_availableFrom", " opening date")}`);
    }
  };

  const handleSetModeOverride = async (homeworkType, mode) => {
    try {
      setError('');
      await setSubmissionModeOverride(selectedCohort, homeworkType, mode);
      setModeOverrides(prev => ({ ...prev, [homeworkType]: mode }));
    } catch (err) {
      console.error('Error setting submission mode override:', err);
      setError(`Failed to update submission mode for ${homeworkType}`);
    }
  };

  const handleClear = async (homeworkType) => {
    try {
      setError('');
      await setDoc(doc(db, 'cohorts', selectedCohort, 'deadlines', 'current'), {
        [homeworkType]: null
      }, { merge: true });
      setDeadlines(prev => {
        const next = { ...prev };
        delete next[homeworkType];
        return next;
      });
    } catch (err) {
      console.error('Error clearing deadline:', err);
      setError(`Failed to clear ${homeworkType.replace("_availableFrom", " opening date")}`);
    }
  };

  const selectedCohortObj = cohorts.find(c => c.id === selectedCohort);
  const enabledTypes = resolveEnabledHomeworkTypes(selectedCohortObj, homeworkTypes);

  const handleToggleEnabledType = async (type, checked) => {
    const current = resolveEnabledHomeworkTypes(selectedCohortObj, homeworkTypes);
    const next = sortHomeworkTypes(checked ? [...current, type] : current.filter(t => t !== type));
    try {
      setError('');
      await setEnabledHomeworkTypes(selectedCohort, next);
      setCohorts(prev => prev.map(c => c.id === selectedCohort ? { ...c, enabledHomeworkTypes: next } : c));
    } catch (err) {
      console.error('Error setting enabled tasks:', err);
      setError('Failed to update enabled tasks for this cohort');
    }
  };

  const handleSelectAllTypes = async () => {
    try {
      setError('');
      await setEnabledHomeworkTypes(selectedCohort, homeworkTypes);
      setCohorts(prev => prev.map(c => c.id === selectedCohort ? { ...c, enabledHomeworkTypes: homeworkTypes } : c));
    } catch (err) {
      console.error('Error selecting all tasks:', err);
      setError('Failed to update enabled tasks for this cohort');
    }
  };

  if (loading) return <div style={{ padding: '16px' }}>Loading cohorts...</div>;

  if (cohorts.length === 0) {
    return (
      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '24px', textAlign: 'center' }}>
        <AlertCircle size={48} color="#9ca3af" style={{ margin: '0 auto 16px' }} />
        <p style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>No cohorts available</p>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>Please create a cohort first in the Cohorts tab.</p>
      </div>
    );
  }

  return (
    <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
        <Calendar size={24} color="#2563eb" />
        <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
          Homework Deadlines{cohortId && selectedCohortObj ? ` - ${selectedCohortObj.name}` : ''}
        </h2>
      </div>

      {/* Cohort Selector */}
      {!cohortId && (
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
          Select Cohort
        </label>
        <select
          value={selectedCohort}
          onChange={e => setSelectedCohort(e.target.value)}
          style={{ ...selectStyle, width: '100%', padding: '12px', fontSize: '15px', border: '2px solid #d1d5db' }}
        >
          {cohorts.map(cohort => (
            <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
          ))}
        </select>
      </div>
      )}

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Task selection - which of the org's homework types this cohort does */}
      <div style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            <ListChecks size={16} color="#2563eb" />
            Tasks for this cohort
          </div>
          {enabledTypes.length < homeworkTypes.length && (
            <button
              onClick={handleSelectAllTypes}
              style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
            >
              Select all
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {homeworkTypes.map(type => (
            <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabledTypes.includes(type)}
                onChange={e => handleToggleEnabledType(type, e.target.checked)}
              />
              {type}
            </label>
          ))}
        </div>
        <p style={{ marginTop: '10px', marginBottom: 0, fontSize: '12px', color: '#6b7280' }}>
          Unchecking a task hides its deadline row below and removes it from this cohort's students' upload options. Its deadline is kept, not deleted, so re-checking it restores what was set.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {enabledTypes.map(type => (
          <DeadlineRow
            key={type}
            type={type}
            savedIso={deadlines[type] || null}
            releaseIso={deadlines[`${type}_availableFrom`] || null}
            onSave={handleSave}
            onClear={handleClear}
            modeOverride={modeOverrides[type] || null}
            onSetModeOverride={handleSetModeOverride}
          />
        ))}
      </div>

      <div style={{ marginTop: '20px', fontSize: '13px', color: '#6b7280', background: '#f9fafb', padding: '12px', borderRadius: '6px' }}>
        Tip: Each date is saved individually when you click Save. Green rows have a deadline set. "Opens" is optional - if set, students can't see, start or download that homework's questions until then; leave it blank for the homework to be open straight away.
      </div>
    </div>
  );
};

export default DeadlineManager;